import type { Card, Priority } from "../data/types";

export type TaskType =
  | "one_off"
  | "recurring_daily"
  | "recurring_weekly"
  | "execution_slot"
  | "contact_batch"
  | "deal"
  | "kpi_event"
  | "unknown";
export type Phase = "p0" | "p1" | "p2" | "p3" | "p4" | "unknown";

export interface TaskMeta {
  taskType: TaskType;
  phase: Phase;
  format: string | null;
  stream: string | null;
  layer: string | null;
  ownerPrimary: string | null;
  ownerSecondary: string | null;
  executionWindow: string | null;
  windowStartMinutes: number | null;
  sla: string | null;
  source: string | null;
  sequence: string | null;
  revenueImpact: string | null;
  segment: string | null;
  account: string | null;
  subreddit: string | null;
}

const PRIORITY_ORDER: Record<Priority, number> = {
  critical: 0,
  high: 1,
  medium: 2,
  low: 3,
};

function getTagValue(tags: string[], prefix: string): string | null {
  const normalizedPrefix = `${prefix}:`;
  const found = tags.find((tag) => tag.startsWith(normalizedPrefix));
  return found ? found.slice(normalizedPrefix.length).trim() : null;
}

function parseNotesKeyValues(notes: string): Map<string, string> {
  const map = new Map<string, string>();
  const chunks = notes
    .split("|")
    .map((chunk) => chunk.trim())
    .filter(Boolean);

  for (const chunk of chunks) {
    const equalIndex = chunk.indexOf("=");
    if (equalIndex <= 0) continue;
    const key = chunk.slice(0, equalIndex).trim().toLowerCase();
    const value = chunk.slice(equalIndex + 1).trim();
    if (key && value) map.set(key, value);
  }

  return map;
}

function parseWindowStartMinutes(window: string | null): number | null {
  if (!window) return null;
  const cleanWindow = window.replace(/_CET$/i, "");
  const [start] = cleanWindow.split("-");
  if (!start) return null;

  const parts = start.split(":");
  if (parts.length !== 2) return null;

  const hour = Number(parts[0]);
  const minute = Number(parts[1]);
  if (!Number.isInteger(hour) || !Number.isInteger(minute)) return null;
  if (hour < 0 || hour > 23 || minute < 0 || minute > 59) return null;

  return hour * 60 + minute;
}

const VALID_TASK_TYPES = new Set<TaskType>([
  "one_off",
  "recurring_daily",
  "recurring_weekly",
  "execution_slot",
  "contact_batch",
  "deal",
  "kpi_event",
]);

function parseTaskType(value: string | null): TaskType {
  if (!value) return "unknown";
  if (VALID_TASK_TYPES.has(value as TaskType)) return value as TaskType;
  return "unknown";
}

function parsePhase(value: string | null): Phase {
  if (!value) return "unknown";
  if (value === "p0" || value === "p1" || value === "p2" || value === "p3" || value === "p4") {
    return value;
  }
  return "unknown";
}

export function getTaskMeta(card: Card): TaskMeta {
  const notesValues = parseNotesKeyValues(card.notes);

  const executionWindow =
    getTagValue(card.tags, "window") ??
    notesValues.get("window_cet") ??
    null;

  const taskType = parseTaskType(getTagValue(card.tags, "task"));
  const phase = parsePhase(getTagValue(card.tags, "phase"));

  return {
    taskType,
    phase,
    format: getTagValue(card.tags, "format"),
    stream: getTagValue(card.tags, "stream"),
    layer: getTagValue(card.tags, "layer"),
    ownerPrimary: getTagValue(card.tags, "owner_primary"),
    ownerSecondary: getTagValue(card.tags, "owner_secondary"),
    executionWindow,
    windowStartMinutes: parseWindowStartMinutes(executionWindow),
    sla: getTagValue(card.tags, "sla") ?? notesValues.get("sla") ?? null,
    source: getTagValue(card.tags, "source"),
    sequence: notesValues.get("sequence") ?? getTagValue(card.tags, "sequence"),
    revenueImpact: notesValues.get("revenue_impact") ?? getTagValue(card.tags, "revenue_impact"),
    segment: getTagValue(card.tags, "segment") ?? notesValues.get("segment") ?? null,
    account: notesValues.get("account") ?? getTagValue(card.tags, "account") ?? null,
    subreddit: notesValues.get("subreddit") ?? getTagValue(card.tags, "subreddit") ?? null,
  };
}

export function hasTaskType(card: Card, taskType: Exclude<TaskType, "unknown">): boolean {
  return getTaskMeta(card).taskType === taskType;
}

export function isOverdue(card: Card, today: string): boolean {
  if (card.status === "done") return false;
  if (!card.targetDate) return false;
  return card.targetDate < today;
}

export function getPhaseSortValue(phase: Phase): number {
  if (phase === "p0") return 0;
  if (phase === "p1") return 1;
  if (phase === "p2") return 2;
  if (phase === "p3") return 3;
  if (phase === "p4") return 4;
  return 99;
}

export function sortCardsByExecution(cards: Card[]): Card[] {
  return [...cards].sort((a, b) => {
    const metaA = getTaskMeta(a);
    const metaB = getTaskMeta(b);

    const dateA = a.targetDate ?? "9999-12-31";
    const dateB = b.targetDate ?? "9999-12-31";
    if (dateA !== dateB) return dateA.localeCompare(dateB);

    const startA = metaA.windowStartMinutes ?? 9999;
    const startB = metaB.windowStartMinutes ?? 9999;
    if (startA !== startB) return startA - startB;

    const priorityDelta = PRIORITY_ORDER[a.priority] - PRIORITY_ORDER[b.priority];
    if (priorityDelta !== 0) return priorityDelta;

    return a.title.localeCompare(b.title);
  });
}
